/**
 * Componente LessonPlan
 * Gerencia o planejamento de aulas, permitindo criar e vincular planejamentos a chamadas.
 * Inclui editor de texto rico, controle de caracteres e listagem de chamadas disponíveis.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import api from '../../../services/api';
import { FaSync } from 'react-icons/fa';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import Modal from 'react-modal';
import Table from '../../Shared/Table';
import "./LessonPlan.css";


const LessonPlan = () => {
    const editorInstance = useRef(null);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [filterCourse, setFilterCourse] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const [courses, setCourses] = useState([]);
    const isMounted = useRef(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [charCount, setCharCount] = useState(0);
    const MAX_CHARS = 8000;
    const [availableCalls, setAvailableCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCalls, setSelectedCalls] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 4
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [teachers, setTeachers] = useState([]);
    const [filterTeacher, setFilterTeacher] = useState('');
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [planModalContent, setPlanModalContent] = useState('');
    const [planModalInfo, setPlanModalInfo] = useState({});
    const [filterStartDate, setFilterStartDate] = useState(getToday());
    const [filterEndDate, setFilterEndDate] = useState(getToday());
    const [showDeletePlanRequestModal, setShowDeletePlanRequestModal] = useState(false);
    const [deletePlanRequestComment, setDeletePlanRequestComment] = useState('');
    const [selectedCallForDeleteRequest, setSelectedCallForDeleteRequest] = useState(null);
    const [deletePlanRequestMessage, setDeletePlanRequestMessage] = useState(null);

    /**
     * Formata a data do formato ISO para DD/MM/YYYY
     */
    const formatDate = (dateString) => {
        try {
            const [date,] = dateString.split('T');
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year} `;
        } catch (error) {
            console.error('Erro ao formatar data:', error);
            return dateString;
        }
    };

    const handleCallSelection = (callId) => {
        if (selectedCalls.includes(callId)) {
            setSelectedCalls(selectedCalls.filter(id => id !== callId));
        } else {
            setSelectedCalls([...selectedCalls, callId]);
        }
    };

    const handleNextPage = () => {
        if (currentPage < pagination.totalPages) {
            fetchCalls(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            fetchCalls(currentPage - 1);
        }
    };

    const handleFirstPage = () => {
        fetchCalls(1);
    };

    const handleLastPage = () => {
        fetchCalls(pagination.totalPages);
    };

    const handleRefresh = async () => {
        await fetchCalls(currentPage);
    };

    /**
     * Busca chamadas com filtros aplicados
     */
    const fetchCalls = useCallback(async (page = 1) => {
        try {
            if (!isMounted.current) return;
            setLoading(true);
            const params = {
                page,
                limit: 5,
            };
            if (filterCourse) {
                params.course_id = filterCourse;
                params.page = 1;
            }
            if (filterPlan === 'withPlan') {
                params.has_open_plan = true;
            } else if (filterPlan === 'withoutPlan') {
                params.has_open_plan = false;
            }
            if (filterTeacher) {
                params.teacher_id = filterTeacher;
            }
            if (filterStartDate) {
                params.start_date = filterStartDate;
            }
            if (filterEndDate) {
                params.end_date = filterEndDate;
            }
            const response = await api.get('/attendances/sessions', { params });

            let formattedCalls = response.data.data.map(session => ({
                id: session.id,
                name: session.course.name,
                date: session.date,
                courseName: session.course.name,
                status: session.status,
                startTime: session.start_time,
                endTime: session.end_time,
                checklist: true,
                has_open_plan: session.has_open_plan
            }));

            if (isMounted.current) {
                setAvailableCalls(formattedCalls);
                setLoading(false);
                setPagination(response.data.pagination);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Erro ao buscar chamadas:', error);
            setLoading(false);
        }
    }, [filterCourse, filterPlan, filterTeacher, filterStartDate, filterEndDate]);

    // Efeito para buscar oficinas
    useEffect(() => {
        isMounted.current = true;
        const fetchCourses = async () => {
            try {
                const response = await api.get('/courses/index');
                if (isMounted.current) {
                    setCourses(response.data.data);
                }
            } catch (error) {
                console.error("Erro ao buscar turmas:", error);
            }
        };

        fetchCourses();
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Efeito para buscar professores
    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                const response = await api.get('/professors/index');
                setTeachers(response.data);
            } catch (error) {
                console.error('Erro ao buscar professores:', error);
            }
        };
        fetchTeachers();
    }, []);

    // Efeito para atualizar chamadas quando os filtros mudarem
    useEffect(() => {
        if (isMounted.current) {
            fetchCalls();
        }
    }, [fetchCalls]);

    useEffect(() => {
        let editor;

        const initializeEditor = () => {
            editor = new EditorJS({
                holder: 'editorjs',
                tools: {
                    header: {
                        class: Header,
                        inlineToolbar: true,
                        config: {
                            placeholder: 'Cabeçalho',
                            levels: [2, 3, 4],
                            defaultLevel: 2
                        }
                    },
                    list: {
                        class: List,
                        inlineToolbar: true,
                        config: {
                            placeholder: 'Lista'
                        }
                    }
                },
                data: {
                    blocks: [
                        {
                            type: "paragraph",
                            data: {
                            }
                        }
                    ]
                },
                onReady: () => {
                    // Remover qualquer formatação HTML residual
                    const editorElement = document.getElementById('editorjs');
                    if (editorElement) {
                        const paragraphs = editorElement.getElementsByTagName('p');
                        Array.from(paragraphs).forEach(p => {
                            p.innerHTML = p.textContent;
                        });
                    }
                },
                onChange: async () => {
                    try {
                        const savedData = await editor.save();
                        const text = savedData.blocks.reduce((acc, block) => {
                            return acc + (block.data.text || '') + ' ';
                        }, '');

                        // Se exceder o limite máximo
                        if (text.length > MAX_CHARS) {
                            // Encontra o último bloco que contém texto
                            const lastBlockWithText = [...savedData.blocks].reverse()
                                .find(block => block.data.text && block.data.text.length > 0);

                            if (lastBlockWithText) {
                                // Mantém o texto no limite exato de MAX_CHARS caracteres
                                const excessChars = text.length - MAX_CHARS;
                                const updatedBlocks = savedData.blocks.map(block => {
                                    if (block === lastBlockWithText) {
                                        return {
                                            ...block,
                                            data: {
                                                ...block.data,
                                                text: lastBlockWithText.data.text.slice(0, -(excessChars))
                                            }
                                        };
                                    }
                                    return block;
                                });

                                // Renderiza o conteúdo atualizado
                                editor.render({ blocks: updatedBlocks });
                                setCharCount(MAX_CHARS);
                            }
                        } else {
                            setCharCount(text.length);
                        }
                    } catch (error) {
                        console.error('Erro ao contar caracteres:', error);
                    }
                },
                placeholder: 'Comece a escrever seu planejamento...',
                i18n: {
                    messages: {
                        ui: {
                            blockTunes: {
                                toggler: {
                                    "Click to tune": "Clique para ajustar",
                                    "or drag to move": "ou arraste para mover"
                                },
                            },
                            inlineToolbar: {
                                converter: {
                                    "Convert to": "Converter para"
                                }
                            },
                            toolbar: {
                                toolbox: {
                                    Add: "Adicionar"
                                }
                            }
                        },
                        toolNames: {
                            Text: "Texto",
                            Heading: "Cabeçalho",
                            List: "Lista",
                            Warning: "Aviso",
                            Checklist: "Lista de Verificação",
                            Quote: "Citação",
                            Code: "Código",
                            Delimiter: "Delimitador",
                            Raw: "HTML Bruto",
                            Table: "Tabela",
                            Link: "Link",
                            Marker: "Marcador",
                            Bold: "Negrito",
                            Italic: "Itálico",
                            InlineCode: "Código Inline",
                            UnorderedList: "Lista não ordenada",
                            OrderedList: "Lista ordenada"
                        },
                        tools: {
                            warning: {
                                Title: "Título",
                                Message: "Mensagem"
                            },
                            link: {
                                "Add a link": "Adicionar um link"
                            },
                            stub: {
                                'The block can not be displayed correctly.': 'O bloco não pode ser exibido corretamente.'
                            }
                        },
                        blockTunes: {
                            delete: {
                                "Delete": "Excluir"
                            },
                            moveUp: {
                                "Move up": "Mover para cima"
                            },
                            moveDown: {
                                "Move down": "Mover para baixo"
                            }
                        }
                    }
                }
            });
            editorInstance.current = editor;
            setIsEditorReady(true);
        };

        const destroyEditor = () => {
            if (editorInstance.current && typeof editorInstance.current.destroy === 'function') {
                editorInstance.current.destroy();
                editorInstance.current = null;
                setIsEditorReady(false);
            }
        };

        const editorElement = document.getElementById('editorjs');

        if (editorElement) {
            if (!isEditorReady) {
                initializeEditor();
            }
        } else {
            const observer = new MutationObserver(() => {
                const editorElement = document.getElementById('editorjs');
                if (editorElement && !isEditorReady) {
                    initializeEditor();
                    observer.disconnect();
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => observer.disconnect();
        }

        return destroyEditor;
    }, [isEditorReady]);

    /**
     * Salva o planejamento e vincula às chamadas selecionadas
     */
    const handleSavePlan = async () => {
        if (editorInstance.current) {
            try {
                // Obter o conteúdo do editor primeiro para validação
                const contentData = await editorInstance.current.save();
                
                // Verifica se há algum texto no planejamento
                const hasContent = contentData.blocks.some(block => 
                    block.data.text && block.data.text.trim().length > 0
                );

                if (!hasContent) {
                    setSaveMessage({
                        type: 'error',
                        text: 'Por favor, escreva o planejamento antes de vincular às chamadas.'
                    });
                    return;
                }

                setIsSaving(true);
                setSaveMessage(null);

                // Preparar o payload
                const payload = {
                    content: JSON.stringify(contentData),
                    char_count: charCount,
                    sessions: selectedCalls.map(callId => ({
                        attendance_session_id: callId
                    }))
                };


                // Enviar para o backend
                const response = await api.post('/lesson-plans', payload);

                if (response.status === 201 || response.status === 200) {
                    setSaveMessage({
                        type: 'success',
                        text: `Planejamento vinculado com sucesso a ${selectedCalls.length} chamada(s)!`
                    });

                    // Limpar o editor e seleções
                    editorInstance.current.clear();
                    setSelectedCalls([]);
                    setCharCount(0);

                    // Atualizar a lista de chamadas
                    fetchCalls(currentPage);
                }

            } catch (error) {
                console.error('Erro ao salvar planejamento:', error);
                setSaveMessage({
                    type: 'error',
                    text: error.response?.data?.message || 'Erro ao vincular o planejamento. Tente novamente.'
                });
            } finally {
                setIsSaving(false);
            }
        }
    };

    // Função para processar o conteúdo do planejamento (EditorJS blocks para HTML simples)
    const renderListItems = (items) => {
        return items.map(item => {
            let html = `<li>${item.content || ''}`;
            if (item.items && item.items.length > 0) {
                html += '<ul>' + renderListItems(item.items) + '</ul>';
            }
            html += '</li>';
            return html;
        }).join('');
    };

    const processLessonPlanContent = (content) => {
        try {
            const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
            let htmlContent = '';
            parsedContent.blocks.forEach(block => {
                switch (block.type) {
                    case 'header':
                        htmlContent += `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
                        break;
                    case 'list':
                        if (block.data.style === 'unordered') {
                            htmlContent += '<ul>' + renderListItems(block.data.items) + '</ul>';
                        } else if (block.data.style === 'ordered') {
                            htmlContent += '<ol>' + renderListItems(block.data.items) + '</ol>';
                        }
                        break;
                    default:
                        if (block.data && block.data.text) {
                            htmlContent += `<p>${block.data.text}</p>`;
                        }
                }
            });
            return htmlContent;
        } catch (error) {
            return '<p>Erro ao carregar o planejamento</p>';
        }
    };

    // Função para abrir modal de visualização do planejamento
    const handleOpenPlanModal = async (call) => {
        try {
            const response = await api.get(`/lesson-plans/${call.id}`);
            setPlanModalContent(processLessonPlanContent(response.data.content));
            setPlanModalInfo({
                courseName: call.courseName,
                date: formatDate(call.date),
                startTime: call.startTime,
                endTime: call.endTime
            });
            setShowPlanModal(true);
        } catch (error) {
            setPlanModalContent('<p>Erro ao carregar o planejamento</p>');
            setShowPlanModal(true);
        }
    };

    // Função para imprimir o planejamento de aula
    const handlePrintLessonPlan = () => {
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        document.body.appendChild(printFrame);
        const printContent = `
            <html>
                <head>
                    <title>Planejamento de Aula</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        .header { margin-bottom: 20px; }
                        .lesson-info { background-color: #f5f5f5; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
                        .content { margin-top: 20px; }
                        ul { list-style-type: disc; padding-left: 20px; }
                        ol { list-style-type: decimal; padding-left: 20px; }
                        li { margin: 5px 0; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Planejamento de Aula</h1>
                    </div>
                    <div class="lesson-info">
                        <p><strong>Turma:</strong> ${planModalInfo?.courseName || 'N/A'}</p>
                        <p><strong>Data da Aula:</strong> ${planModalInfo?.date || 'N/A'}</p>
                        <p><strong>Horário:</strong> ${planModalInfo?.startTime || ''} - ${planModalInfo?.endTime || ''}</p>
                    </div>
                    <div class="content">
                        ${planModalContent}
                    </div>
                </body>
            </html>
        `;
        printFrame.contentWindow.document.open();
        printFrame.contentWindow.document.write(printContent);
        printFrame.contentWindow.document.close();
        printFrame.onload = () => {
            try {
                printFrame.contentWindow.print();
            } catch (error) {
                // Ignorar erro
            } finally {
                setTimeout(() => {
                    document.body.removeChild(printFrame);
                }, 100);
            }
        };
    };

    // Função para abrir modal de solicitação de exclusão
    const handleShowDeletePlanRequestModal = (call) => {
        setSelectedCallForDeleteRequest(call);
        setDeletePlanRequestComment('');
        setDeletePlanRequestMessage(null);
        setShowDeletePlanRequestModal(true);
    };
    console.log('selectedCallForDeleteRequest', handleShowDeletePlanRequestModal);

    // Função para enviar solicitação de exclusão
    const handleDeletePlanRequest = async () => {
        if (!deletePlanRequestComment.trim()) {
            setDeletePlanRequestMessage({ type: 'error', text: 'Por favor, informe o motivo da solicitação.' });
            return;
        }
        try {
            const response = await api.post(`/lesson-plans/${selectedCallForDeleteRequest.id}/request-delete`, {
                comment: deletePlanRequestComment
            });
            if (response.status === 200 || response.status === 201) {
                setDeletePlanRequestMessage({ type: 'success', text: 'Solicitação enviada com sucesso!' });
                setTimeout(() => {
                    setShowDeletePlanRequestModal(false);
                }, 1200);
            } else {
                setDeletePlanRequestMessage({ type: 'error', text: 'Erro ao enviar solicitação.' });
            }
        } catch (error) {
            setDeletePlanRequestMessage({ type: 'error', text: error.response?.data?.message || 'Erro ao enviar solicitação.' });
        }
    };

    // Função para excluir o planejamento de aula diretamente
    const handleDeletePlan = async (call) => {
        if (!window.confirm('Tem certeza que deseja excluir este planejamento de aula? Esta ação não pode ser desfeita.')) return;
        try {
            const response = await api.delete(`/lesson-plan/${call.id}/delete`);
            if (response.status === 200 || response.status === 204) {
                setSaveMessage({ type: 'success', text: 'Planejamento excluído com sucesso!' });
                setShowPlanModal(false);
                setShowDeletePlanRequestModal(false);
                handleRefresh();
            } else {
                setSaveMessage({ type: 'error', text: 'Erro ao excluir o planejamento.' });
            }
        } catch (error) {
            setSaveMessage({ type: 'error', text: error.response?.data?.message || 'Erro ao excluir o planejamento.' });
        }
    };

    // Atualizar getActionItems para incluir a ação de exclusão direta
    const getActionItems = (id, call) => {
        const actions = [];
        if (call.has_open_plan) {
            actions.push({
                label: 'Visualizar Planejamento',
                action: () => handleOpenPlanModal(call)
            });
            actions.push({
                label: 'Excluir Planejamento',
                action: () => handleDeletePlan(call)
            });
        }
        return actions;
    };

    return (
        <div className="list-containet-tab">
            <div className="lesson-plan-content">
                <div className="plan-area">
                    <div className="plan-header">
                        <div className="plan-header-left">
                            <h2>Planejamento de Aula</h2>
                            <div className="char-counter">
                                <span className={charCount > MAX_CHARS * 0.9 ? "char-warning" : ""}>
                                    {charCount}/{MAX_CHARS} caracteres
                                </span>
                            </div>
                        </div>
                        <div className="plan-actions">
                            <button
                                className="save-plan-button"
                                onClick={handleSavePlan}
                                disabled={isSaving || selectedCalls.length === 0 || charCount === 0}
                            >
                                {isSaving ? 'Salvando...' : 'Vincular Planejamento'}
                            </button>
                        </div>
                    </div>
                    {saveMessage && (
                        <div className={`save-message ${saveMessage.type}`}>
                            {saveMessage.text}
                        </div>
                    )}
                    <div id="editorjs" className="editor-container" placeholder="Comece a escrever seu planejamento..."></div>
                    {charCount > MAX_CHARS * 0.9 && (
                        <div className="char-limit-warning">
                            {charCount >= MAX_CHARS
                                ? "Limite máximo de caracteres atingido!"
                                : "Próximo ao limite máximo de caracteres!"}
                        </div>
                    )}
                </div>

                {/* Right Side - Filters and Calls List */}
                <div className="sidebar-area">
                    <div className="filters-refresh-container">
                        <div className="filters-container">
                            <div className="filter-group mobile-full-width">
                                <fieldset>
                                    <legend>Turma:</legend>
                                    <select
                                        id="filter-course"
                                        value={filterCourse}
                                        onChange={(e) => setFilterCourse(e.target.value)}
                                    >
                                        <option value="">Todas as turmas</option>
                                        {Array.isArray(courses) && courses.map((course) => (
                                            <option key={course.id} value={course.id}>
                                                {course.name}
                                            </option>
                                        ))}
                                    </select>
                                </fieldset>
                            </div>

                            <div className="filter-group mobile-full-width">
                                <fieldset>
                                    <legend>Planejamento:</legend>
                                    <select
                                        id="filter-plan"
                                        value={filterPlan}
                                        onChange={(e) => setFilterPlan(e.target.value)}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="withPlan">Com planejamento</option>
                                        <option value="withoutPlan">Sem planejamento</option>
                                    </select>
                                </fieldset>
                            </div>
                            <div className="filter-group mobile-full-width">
                                <fieldset>
                                    <legend>Professor:</legend>
                                    <select
                                        id="filter-teacher"
                                        value={filterTeacher}
                                        onChange={e => setFilterTeacher(e.target.value)}
                                    >
                                        <option value="">Todos os professores</option>
                                        {Array.isArray(teachers) && teachers.map(teacher => (
                                            <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                                        ))}
                                    </select>
                                </fieldset>
                            </div>
                            <div className="filter-group mobile-full-width">
                                <fieldset>
                                    <legend>Data Inicial:</legend>
                                    <input
                                        type="date"
                                        value={filterStartDate}
                                        onChange={e => setFilterStartDate(e.target.value)}
                                    />
                                </fieldset>
                            </div>
                            <div className="filter-group mobile-full-width">
                                <fieldset>
                                    <legend>Data Final:</legend>
                                    <input
                                        type="date"
                                        value={filterEndDate}
                                        onChange={e => setFilterEndDate(e.target.value)}
                                    />
                                </fieldset>
                            </div>
                            <button
                                className="refresh-button mobile-full-width"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                {loading ? 'Carregando...' : <><FaSync /> Atualizar</>}
                            </button>
                        </div>
                    </div>
                    <div className="calls-area">
                        <h2>Vincular Chamadas</h2>
                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                {availableCalls.length > 0 ? (
                                    <Table
                                        data={availableCalls}
                                        columns={[
                                            {
                                                key: 'select',
                                                label: '',
                                                render: (value, row) =>
                                                    !row.has_open_plan ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCalls.includes(row.id)}
                                                            onChange={() => handleCallSelection(row.id)}
                                                        />
                                                    ) : null
                                            },
                                            { key: 'name', label: 'Turma' },
                                            { key: 'date', label: 'Data', render: (date) => formatDate(date) },
                                            { key: 'startTime', label: 'Início' },
                                            { key: 'endTime', label: 'Término' },
                                            {
                                                key: 'has_open_plan',
                                                label: 'Planejamento',
                                                render: (hasOpenPlan) => (
                                                    <span className={hasOpenPlan ? 'status-open' : 'status-closed'}>
                                                        {hasOpenPlan ? 'sim' : 'Não'}
                                                    </span>
                                                )
                                            }
                                        ]}
                                        itemsPerPage={pagination.itemsPerPage}
                                        currentPage={currentPage}
                                        loading={loading}
                                        getActionItems={getActionItems}
                                    />
                                ) : (
                                    <div className="no-calls">Nenhuma chamada aberta disponível</div>
                                )}

                                {pagination.totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            className="pagination-button"
                                            onClick={handleFirstPage}
                                            disabled={currentPage === 1}
                                        >
                                            {"<<"}
                                        </button>
                                        <button
                                            className="pagination-button"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            {"<"}
                                        </button>
                                        <span className="page-info">
                                            Página {currentPage} de {pagination.totalPages}
                                        </span>
                                        <button
                                            className="pagination-button"
                                            onClick={handleNextPage}
                                            disabled={currentPage === pagination.totalPages}
                                        >
                                            {">"}
                                        </button>
                                        <button
                                            className="pagination-button"
                                            onClick={handleLastPage}
                                            disabled={currentPage === pagination.totalPages}
                                        >
                                            {">>"}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {/* Modal de visualização do planejamento */}
            <Modal
                isOpen={showPlanModal}
                onRequestClose={() => setShowPlanModal(false)}
                contentLabel="Planejamento de Aula"
                className="lesson-plan-modal"
                overlayClassName="lesson-plan-modal-overlay"
                ariaHideApp={false}
            >
                <h2>Planejamento de Aula</h2>
                <div className="lesson-info">
                    <p><strong>Turma:</strong> {planModalInfo.courseName || 'N/A'}</p>
                    <p><strong>Data da Aula:</strong> {planModalInfo.date || 'N/A'}</p>
                    <p><strong>Horário:</strong> {planModalInfo.startTime} - {planModalInfo.endTime}</p>
                </div>
                <div dangerouslySetInnerHTML={{ __html: planModalContent }} />
                <button onClick={handlePrintLessonPlan} className="print-button">Imprimir</button>
                <button onClick={() => setShowPlanModal(false)} className="close-btn">Fechar</button>
            </Modal>

            {/* Modal de solicitação de exclusão */}
            <Modal
                isOpen={showDeletePlanRequestModal}
                onRequestClose={() => setShowDeletePlanRequestModal(false)}
                contentLabel="Solicitar Exclusão do Planejamento"
                className="lesson-plan-modal"
                overlayClassName="lesson-plan-modal-overlay"
                ariaHideApp={false}
            >
                <h2>Solicitar Exclusão do Planejamento</h2>
                <div className="lesson-info">
                    <p><strong>Turma:</strong> {selectedCallForDeleteRequest?.courseName || 'N/A'}</p>
                    <p><strong>Data da Aula:</strong> {selectedCallForDeleteRequest ? formatDate(selectedCallForDeleteRequest.date) : 'N/A'}</p>
                    <p><strong>Horário:</strong> {selectedCallForDeleteRequest?.startTime} - {selectedCallForDeleteRequest?.endTime}</p>
                </div>
                <textarea
                    placeholder="Descreva o motivo da solicitação de exclusão..."
                    value={deletePlanRequestComment}
                    onChange={e => setDeletePlanRequestComment(e.target.value)}
                    rows={4}
                    style={{ width: '100%', marginBottom: 10 }}
                />
                {deletePlanRequestMessage && (
                    <div className={`save-message ${deletePlanRequestMessage.type}`}>{deletePlanRequestMessage.text}</div>
                )}
                <button onClick={handleDeletePlanRequest} className="save-plan-button">Enviar Solicitação</button>
                <button onClick={() => setShowDeletePlanRequestModal(false)} className="close-btn">Cancelar</button>
            </Modal>
        </div>
    );
};

export default LessonPlan;

// Função utilitária para obter a data de hoje no formato YYYY-MM-DD
const getToday = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};